<template>
    <div
        class="box-border flex flex-col w-full px-2 py-4 bg-white border-0 border-black border-solid rounded-lg h-fit"
    >
        <div class="box-border w-full">
            <div class="flex flex-col py-4 grow">
                <div class="box-border">
                    <label>
                        <input
                            :style="playerStatusColor"
                            @input="onProgressSlided"
                            :disabled="!videoUrl"
                            v-model="currentPlayingAt"
                            type="range"
                            :max="videoDuration || 0"
                            class="w-full h-1 bg-gray-600 cursor-pointer"
                        />
                    </label>
                </div>
                <iframe
                    class="hidden"
                    frameborder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowfullscreen
                    ref="iFrame" muted="true"></iframe>
                <div class="flex flex-row grow">
                    <span class="w-full grow">{{ convertToMinuteAndSecond(currentPlayingAt) }}</span>
                    <span class="text-right"> {{ convertToMinuteAndSecond(videoDuration) }}</span>
                </div>
            </div>
        </div>
        <div
            class="flex flex-row items-center justify-center w-full gap-8 player-options"
        >
            <button
                @click="getPrev"
                :disabled="!isPrevAvailable || !videoUrl"
            >
                <img src="/img/icons/skip_to_prev.svg" class="h-10 aspect-square">
            </button>
            <main class="flex flex-row justify-center">
                <button
                    @click="playVideo"
                    :disabled="!videoUrl"
                    v-if="!isPlaying"
                >
                    <img src="/img/icons/play_circle.svg" class="h-10 aspect-square">
                </button>

                <button @click="pauseVideo" v-else>
                    <img src="/img/icons/pause_circle.svg" class="h-10 aspect-square">
                </button>
            </main>
            <button
                @click="getNext"
                :disabled="!isNextAvailable || !videoUrl"
            >
                <img src="/img/icons/skip_to_next.svg" class="h-10 aspect-square">
            </button>
        </div>
    </div>
</template>

<script>
    import Vimeo from "@vimeo/player";

    export default {
        name: "AudioBookPlayer",
        props: {
            isNextAvailable: {
                type: Boolean,
                default: () => false
            },
            isPrevAvailable: {
                type: Boolean,
                default: () => false
            },
            videoUrl: {
                type: String,
                default: () => ""
            },
            value: {
                type: Number,
                default: () => 1
            },
            autoPlay: {
                type: Number,
                default: () => 0
            },
            currentAudioId: {
                type: Number,
                default: () => 0
            },
            updatedIndex: {
                type: Number,
                default: () => 0
            }
        },
        emits: [
            "input",
            "nextClicked",
            "prevClicked",
            "resetCurrentPlaying"
        ],
        data() {
            return {
                iFrame: null,
                // player: null, // if uncommented error of vimeo player may be unloaded
                isPlaying: null,
                videoDuration: 0,
                playBackSpeed: 1,
                currentVideoTimeStamp: 0,
                playBackSpeedOptionSelected: "normal",
                playBackSpeedOptions: {
                    slower: 0.7,
                    slow: 0.8,
                    normal: 1,
                    fast: 1.25,
                    faster: 1.5
                },
                options: {
                    muted: true,
                },
                device: '',
            };
        },
        created(){
            this.getMobileOS();
        },
        computed: {
            playerStatusColor: function () {
                let percentage = (this.currentPlayingAt * 1.3) / this.videoDuration;
                return `background:linear-gradient(to right, rgb(157 23 77 ) ,gray ${
                    isNaN(percentage) ? 0 : percentage * 100
                }%)`;
            },
            // playback speed
            playBackSpeedSelected: {
                set(e) {
                    this.playBackSpeedOptionSelected = "normal";
                    // setting playback speed and check it doesn't go out of our available options
                    if (this.playBackSpeedOptions[e]) {
                        this.playBackSpeedOptionSelected = e;
                    }
                    this.playBackSpeed =
                        this.playBackSpeedOptions[this.playBackSpeedOptionSelected];
                    // videoEl.playbackRate = playBackSpeed;
                },
                get() {
                    return this.playBackSpeedOptionSelected;
                }
            },
            // current audiobook time stamp
            currentPlayingAt: {
                get() {
                    return this.currentVideoTimeStamp;
                },
                set(e) {
                    if (!this.videoUrl || !this.videoDuration) {
                        return this.$emit("input", 0);
                    }
                    this.currentVideoTimeStamp = e || 0;
                    this.$emit("input", parseInt(e));
                }
            }
        },
        methods: {
            checkDevice(){
                const ua = navigator.userAgent;
                if (/android/i.test(ua)) {
                    return "android";
                } else if (/windows/i.test(ua)) {
                    return "windows";
                } else if (/Macintosh/i.test(ua)) {
                    return "macbook";
                } else if (/linux/i.test(ua)) {
                    return "linux";
                } else if (/iPad|iPhone|iPod/i.test(ua)) {
                    return "ios";
                } 
                return "other";          
            },
            getMobileOS() {
                this.device = this.checkDevice();
            },
            // function to pause audiobook
            pauseVideo() {
                this.isPlaying = false;
                if (!this.player) {
                    return;
                }
                this.player.pause();
            },
            // function to play audiobook
            playVideo() {
                this.isPlaying = false;
                if (!this.player) {
                    return;
                }
                this.player.play();
                this.isPlaying = true;
            },
            // function to emit nextClicked event to get next audioBook Url
            getNext() {
                return this.$emit("nextClicked");
            },
            // function to emit prevClicked event to get previous audioBook Url
            getPrev() {
                return this.$emit("prevClicked");
            },
            // change audiobook current time using progress bar
            onProgressSlided(e) {
                this.player.setCurrentTime(this.currentPlayingAt = e.target.value);
            },
            // change minutes elpased into mm:ss format
            convertToMinuteAndSecond(time) {
                let min = parseInt(time / 60);
                let seconds = parseInt(time % 60);
                if (min < 10) {
                    min = "0" + min;
                }
                if (seconds < 10) {
                    seconds = "0" + seconds;
                }
                return `${min}:${seconds}`;
            },
            playerTimeUpdateHandler(e) {
                this.currentPlayingAt = e.seconds;
                this.$emit("time", e.seconds);
            },
            chapterEndedHandler() {
                this.$emit("ended")
            }
        },
        async mounted() {
            this.iFrame = this.$refs.iFrame;
            this.iFrame.src = this.videoUrl+'&mute=1';
            this.player = await new Vimeo(this.iFrame, this.options);
            this.videoDuration = parseInt((await this.player.getDuration()).toString());
            if(this.autoPlay == 1) {
                if(this.device !== 'ios') {
                    await this.playVideo();
                }
            }
            this.player.on("timeupdate", this.playerTimeUpdateHandler);

            if(this.$cookies.get("audioId") !== null && this.$cookies.get("chapterId") && this.$cookies.get("chapterIndex") !== null && this.$cookies.get("chapterTime") !== null) {
                if(this.currentAudioId == Number(this.$cookies.get("audioId")) && this.updatedIndex == Number(this.$cookies.get("chapterIndex"))) {
                    this.player.setCurrentTime(this.currentPlayingAt = Number(this.$cookies.get("chapterTime")));
                }
            }
            this.player.on("ended", this.chapterEndedHandler);
        },
        unmounted() {
            if (this.player) {
                this.player.destroy();
            }
            this.currentPlayingAt = 0;
            this.$emit("resetCurrentPlaying");
        }
    };
</script>

<style scoped>
    input[type="range"] {
        appearance: none;
    }
</style>
